/**
 * Insurance billing configuration
 *
 * Defines billing types, explain/checkout route segments, and meta shapes used by
 * routing and checkout UI for GKV invoice, blocked, and Abtretung flows.
 */

import type { Insurance } from '@physiofit-digital/physiofit_types';
import type { OnboardingCheckoutTableCopy } from '../components/content/OnboardingCheckoutTable';

export type InsuranceBillingPatientType = NonNullable<Insurance['billingPatient']>['type'];

/** All billing flows have dedicated screens; `bnpl` re-enabled in ERS-5. */
export type InsuranceBillingType = InsuranceBillingPatientType;

export type InvoiceExplainTimelineMeta = {
  bonusTitle: string;
  bonusBody: string;
};

export type InvoiceCheckoutMeta = {
  headline: string;
  disclaimer: string;
  detailLinkLabel: string;
};

export type BlockedExplainMeta = {
  title: string;
  body: string;
};

export type AbtretungExplainMeta = {
  headlineTemplate: string;
  timeline: {
    step1Title: string;
    step1Body: string;
    step2Title: string;
    step2BodyTemplate: string;
  };
};

export type WithoutAbtretungPatientExplainMeta = {
  headlineTemplate: string;
  timeline: {
    step1Title: string;
    step1Body: string;
    step2Title: string;
    step2BodyTemplate: string;
    step3Title: string;
    step3BodyTemplate: string;
    step4Title: string;
    step4BodyTemplate: string;
  };
};

export type WithoutAbtretungPhysiofitExplainMeta = {
  headlineTemplate: string;
  timeline: {
    step1Title: string;
    step1Body: string;
    step2Title: string;
    step2BodyTemplate: string;
  };
};

export type CheckoutTableConfig = {
  courseFeeEur: number;
  courseFeeLabel: string;
  reimbursementEur: number;
  reimbursementLabel: string;
  subsidyEur: number;
  subsidyLabel: string;
  bonusEur: number;
  bonusLabel: string;
  yourCostsEur: number;
  yourCostsLabel: string;
  footerLabel: string;
  a11ySummary: string;
  showPhysiofitSubsidy: boolean;
  showFooter: boolean;
};

export type CheckoutMeta = {
  table: CheckoutTableConfig;
};

export type InsuranceMeta = {
  invoiceExplain?: InvoiceExplainTimelineMeta;
  invoiceCheckout?: InvoiceCheckoutMeta;
  blockedExplain?: BlockedExplainMeta;
  abtretungExplain?: AbtretungExplainMeta;
  withoutAbtretungPhysiofitExplain?: WithoutAbtretungPhysiofitExplainMeta;
  withoutAbtretungPatientExplain?: WithoutAbtretungPatientExplainMeta;
  checkout?: CheckoutMeta;
};

export type BillingTypeRouteConfig = {
  explainRouteSegment: string;
  forwardRouteSegment: string;
};

export const billingTypeRoutes: Record<InsuranceBillingType, BillingTypeRouteConfig> = {
  invoice: {
    explainRouteSegment: 'insurance-invoice-explain',
    forwardRouteSegment: 'insurance-invoice-checkout',
  },
  blocked: {
    explainRouteSegment: 'insurance-blocked-free-process-overview',
    forwardRouteSegment: 'install',
  },
  abtretung: {
    explainRouteSegment: 'insurance-abtretung-process-overview',
    forwardRouteSegment: 'install',
  },
  without_abtretung_physiofit: {
    explainRouteSegment: 'insurance-without-abtretung-physiofit-process-overview',
    forwardRouteSegment: 'insurance-without-abtretung-physiofit-checkout',
  },
  without_abtretung_patient: {
    explainRouteSegment: 'insurance-without-abtretung-patient-process-overview',
    forwardRouteSegment: 'insurance-without-abtretung-patient-checkout',
  },
  bnpl: {
    explainRouteSegment: 'insurance-bnpl-process-overview',
    forwardRouteSegment: 'insurance-bnpl-checkout',
  },
};

/**
 * BNPL checkout segment. Since ERS-61 merged the payment overview into the explain screen this is
 * also `bnpl`'s `forwardRouteSegment`; kept as its own constant for the callers that name it directly.
 */
export const BNPL_CHECKOUT_ROUTE_SEGMENT = 'insurance-bnpl-checkout';
/** Payment method, consents and submit (ERS-78 screen 09), the checkout's forward route segment. */
export const BNPL_PAYMENT_ROUTE_SEGMENT = 'insurance-bnpl-payment';
export const BNPL_REJECTED_ROUTE_SEGMENT = 'insurance-bnpl-rejected';

export type InsuranceBlockedReason = NonNullable<Insurance['billingPatient']>['blockedReason'];

export const BLOCKED_TEMP_EXPLAIN_REASONS = [
  'refundBelowHundred',
  'tempBlocked',
] as const satisfies readonly InsuranceBlockedReason[];

export const BLOCKED_TEMP_EXPLAIN_ROUTE_SEGMENT = 'insurance-blocked-temp-process-overview';

/** Written by the BNPL data screen for a patient under 18. An internal code, never shown to the patient. */
export const MINOR_BLOCKED_REASON = 'underEighteen';

/**
 * The stored blocked reason as patient-facing body copy, or undefined when empty or the internal minor code.
 *
 * @param {string | null | undefined} blockedReason Reason from the invitation.
 * @return {string | undefined} Text to show, if any.
 */
export function readDisplayableBlockedReason(blockedReason?: string | null): string | undefined {
  const reason = blockedReason?.trim();
  return reason && reason !== MINOR_BLOCKED_REASON ? reason : undefined;
}

/** Post-insurance screen shown when the course itself is not certified. */
export const COURSE_NOT_CERTIFIED_ROUTE_SEGMENT = 'course-not-certified-process-overview';

/** Practice-fee variant of the blocked explain screen (PF-5963). */
export const BLOCKED_PRACTICE_FEE_EXPLAIN_ROUTE_SEGMENT = 'insurance-blocked-practice-fee-process-overview';

export type BlockedExplainVariant = {
  blockedReason?: string | null;
  /** Resolved at insurance selection and read back from `insuranceInfo.practiceChargesFee`. */
  practiceChargesFee?: boolean;
};

/**
 * Whether the blocked reason uses the temporary-blocked explain screen.
 *
 * @param {string | null | undefined} blockedReason Blocked reason from insurer or patient doc.
 * @return {boolean} True for refund-below-hundred or temp-blocked reasons.
 */
export function isBlockedTempExplainReason(blockedReason?: string | null): boolean {
  const reason = blockedReason?.trim();
  return reason === 'refundBelowHundred' || reason === 'tempBlocked';
}

/**
 * Picks the blocked explain route segment: practice fee, temporary blocked, or free.
 *
 * The practice fee wins over the temp variant. No private insurer carries a `billingPatient` block in the
 * catalog, so the two cannot collide today; the order is fixed here so a future catalog change cannot
 * silently send a fee-charging practice's patient to the free screen.
 *
 * @param {Object} variant Blocked reason and the persisted practice-fee decision.
 * @return {string} Route segment under `/webAppOnboarding/`.
 */
export function resolveBlockedExplainRouteSegment({
  blockedReason,
  practiceChargesFee,
}: BlockedExplainVariant): string {
  if (practiceChargesFee) {
    return BLOCKED_PRACTICE_FEE_EXPLAIN_ROUTE_SEGMENT;
  }
  if (isBlockedTempExplainReason(blockedReason)) {
    return BLOCKED_TEMP_EXPLAIN_ROUTE_SEGMENT;
  }
  return billingTypeRoutes.blocked.explainRouteSegment;
}

export type ResolvedCheckoutTable = OnboardingCheckoutTableCopy & {
  showPhysiofitSubsidy: boolean;
  showFooter: boolean;
};

/**
 * Total progress steps per billing flow. `bnpl` runs a third screen (financing, data, payment)
 * after insurance selection; `invoice` and the `without_abtretung_*` flows run two (explain +
 * checkout); the rest end after the explain screen. Centralized so the Install screen (the one
 * screen every flow ends on) can report the right counter per flow.
 *
 * @param {string | null} billingType Effective billing flow, or null before it is resolved.
 * @return {number} Total step count for the progress indicator.
 */
export function resolveOnboardingTotalSteps(billingType: InsuranceBillingType | null): number {
  if (billingType === 'bnpl') {
    return 7;
  }
  if (
    billingType === 'invoice' ||
    billingType === 'without_abtretung_patient' ||
    billingType === 'without_abtretung_physiofit'
  ) {
    return 6;
  }
  return 5;
}
